import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Alert, Spin, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  createSurveyTierCheckoutSession,
  mountSurveyStripeCheckout,
  refreshUserSubscription,
} from "util/surveyStripe";
import {
  SURVEY_BILLING_MONTHLY,
  surveyPriceDisplay,
} from "util/surveyBilling";
import ProspectBillingToggle from "pages/SonicProspect/ProspectBillingToggle";
import toggleStyles from "pages/SonicProspect/prospectBillingToggle.module.scss";
import styles from "components/Alerts/alertLimitUpgrade.module.scss";

const SonicSurveyUpgradeModal = ({
  open,
  onClose,
  mode = "plus",
  returnPath = "/console/surveys",
  onSuccess,
  user,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("intro");
  const [errorMessage, setErrorMessage] = useState("");
  const [billingCycle, setBillingCycle] = useState(SURVEY_BILLING_MONTHLY);
  const checkoutRef = useRef(null);
  const embeddedCheckoutRef = useRef(null);

  const titleKey =
    mode === "pro_plus"
      ? "surveyManagement.upgrade_pro_plus_title"
      : mode === "pro"
        ? "surveyManagement.upgrade_pro_title"
        : "surveyManagement.upgrade_plus_title";

  const bodyKey =
    mode === "pro_plus"
      ? "surveyManagement.upgrade_pro_plus_body"
      : mode === "pro"
        ? "surveyManagement.upgrade_pro_body"
        : "surveyManagement.upgrade_plus_body";

  const destroyEmbedded = useCallback(() => {
    if (embeddedCheckoutRef.current?.destroy) {
      embeddedCheckoutRef.current.destroy();
      embeddedCheckoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    destroyEmbedded();
    setStep("intro");
    setErrorMessage("");
    setLoading(false);
    setBillingCycle(SURVEY_BILLING_MONTHLY);
  }, [destroyEmbedded]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const completeSubscriptionUpdate = useCallback(
    async () => {
      message.success(
        t("surveyManagement.upgrade_success", "Your plan was updated."),
      );
      if (user?.uid) {
        await refreshUserSubscription(user.uid);
      }
      if (onSuccess) onSuccess();
      onClose();
    },
    [onClose, onSuccess, t, user?.uid],
  );

  const beginCheckout = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const embeddedRes = await createSurveyTierCheckoutSession({
        tier: mode,
        returnPath,
        checkoutUi: "embedded",
        billingInterval: billingCycle,
      });

      if (embeddedRes?.success?.mode === "subscription_updated") {
        await completeSubscriptionUpdate();
        return;
      }

      if (embeddedRes?.success?.client_secret && embeddedRes.success.publishable_key) {
        setStep("checkout");
        return;
      }

      const redirectRes = await createSurveyTierCheckoutSession({
        tier: mode,
        returnPath,
        checkoutUi: "redirect",
        billingInterval: billingCycle,
      });

      if (redirectRes?.success?.mode === "subscription_updated") {
        await completeSubscriptionUpdate();
        return;
      }

      if (redirectRes?.success) {
        await mountSurveyStripeCheckout(null, redirectRes.success);
        return;
      }

      setErrorMessage(
        redirectRes?.error ||
          embeddedRes?.error ||
          t("surveyManagement.upgrade_checkout_failed"),
      );
    } catch (err) {
      setErrorMessage(err?.message || t("surveyManagement.upgrade_checkout_failed"));
    } finally {
      setLoading(false);
    }
  }, [billingCycle, completeSubscriptionUpdate, mode, returnPath, t]);

  useEffect(() => {
    if (step !== "checkout" || !checkoutRef.current) return undefined;
    let cancelled = false;

    (async () => {
      const embeddedRes = await createSurveyTierCheckoutSession({
        tier: mode,
        returnPath,
        checkoutUi: "embedded",
        billingInterval: billingCycle,
      });
      if (cancelled || !embeddedRes?.success) return;
      const mounted = await mountSurveyStripeCheckout(
        checkoutRef.current,
        embeddedRes.success,
      );
      if (!cancelled) embeddedCheckoutRef.current = mounted.checkout;
    })();

    return () => {
      cancelled = true;
      destroyEmbedded();
    };
  }, [billingCycle, step, mode, returnPath, destroyEmbedded]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={step === "checkout" ? 480 : 420}
      destroyOnClose
      title={t(titleKey)}
    >
      {step === "intro" ? (
        <>
          <p>{t(bodyKey)}</p>
          <div className={toggleStyles["prospect-billing-toggle"]}>
            <ProspectBillingToggle billingCycle={billingCycle} onChange={setBillingCycle} t={t} />
          </div>
          <div style={{ margin: "12px 0 16px", textAlign: "center" }}>
            {(() => {
              const display = surveyPriceDisplay(mode, billingCycle);
              if (!display) return null;
              return (
                <>
                  <p style={{ margin: 0, fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{display.headline}</p>
                  {display.period ? (
                    <p style={{ margin: "4px 0 0", fontSize: 14, color: "rgba(0,0,0,0.65)" }}>{display.period}</p>
                  ) : null}
                  {display.saveNote ? (
                    <p style={{ margin: "8px 0 0", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#0891b2" }}>
                      {display.saveNote}
                    </p>
                  ) : null}
                </>
              );
            })()}
          </div>
          {errorMessage ? (
            <Alert type="error" message={errorMessage} showIcon className={styles.errorAlert} />
          ) : null}
          <div className={styles.actions}>
            <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
            <Button type="primary" loading={loading} onClick={beginCheckout}>
              {t("surveyManagement.upgrade_checkout_cta")}
            </Button>
          </div>
        </>
      ) : (
        <>
          {errorMessage ? (
            <Alert type="error" message={errorMessage} showIcon className={styles.errorAlert} />
          ) : null}
          <Spin spinning={loading}>
            <div ref={checkoutRef} className={styles.embeddedCheckout} />
          </Spin>
        </>
      )}
    </Modal>
  );
};

export default SonicSurveyUpgradeModal;
